import { useState } from 'react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { c, money, computeLine, computeTotals, inputStyle, labelStyle } from './cajeroShared';

// Editor de nota crédito/débito: se parte de una factura existente, se ajustan
// las líneas y se indica el motivo.
function NoteEditor({ token, noteType, typeLabel, onBack }) {
  const isMobile = useIsMobile();
  const authH = { Authorization: `Bearer ${token}` };
  const jsonH = { ...authH, 'Content-Type': 'application/json' };

  const [facturaQuery, setFacturaQuery] = useState('');
  const [matches, setMatches] = useState(null);
  const [factura, setFactura] = useState(null);
  const [items, setItems] = useState([]);
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState(null);

  const totals = computeTotals(items);
  const setItem = (i, patch) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const removeItem = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const searchFactura = async () => {
    const q = facturaQuery.trim();
    if (!q) return;
    setMsg(null);
    try {
      const r = await fetch(`/api/cajero/docs?type=factura&q=${encodeURIComponent(q)}`, { headers: authH });
      const d = await r.json();
      setMatches(d.data || []);
      if (!d.data?.length) setMsg({ ok: false, text: 'No se encontró ninguna factura con ese dato.' });
    } catch { setMsg({ ok: false, text: 'Error al buscar' }); }
  };

  const pickFactura = async (id) => {
    try {
      const r = await fetch(`/api/cajero/docs/factura/${id}`, { headers: authH });
      const d = await r.json();
      setFactura(d.data);
      setItems((d.items || []).map((it) => ({
        product_ref: it.product_ref || '', name: it.name || '', description: it.description || '',
        quantity: it.quantity, unit_price: it.unit_price, iva_percent: it.iva_percent,
        retefuente_percent: it.retefuente_percent, impoconsumo_percent: it.impoconsumo_percent,
      })));
      setMatches(null);
    } catch { setMsg({ ok: false, text: 'Error al cargar la factura' }); }
  };

  const submit = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch('/api/cajero/notas', {
        method: 'POST', headers: jsonH,
        body: JSON.stringify({ note_type: noteType, factura_id: factura.id, motivo, items }),
      });
      const d = await r.json();
      if (r.ok) setResult(d);
      else setMsg({ ok: false, text: d.message || 'No se pudo crear' });
    } catch { setMsg({ ok: false, text: 'Error de conexión' }); }
    finally { setBusy(false); }
  };

  const print = async () => {
    try {
      const r = await fetch(`/api/cajero/docs/${noteType}/${result.id}/print`, { headers: authH });
      const d = await r.json();
      if (r.ok && d.url) window.open(d.url, '_blank');
      else setMsg({ ok: false, text: d.message || 'No se pudo imprimir' });
    } catch { setMsg({ ok: false, text: 'Error al imprimir' }); }
  };

  if (result) {
    return (
      <div style={{ maxWidth: 620, margin: '0 auto', padding: '2rem 1rem', textAlign: 'center' }}>
        <div style={{ fontSize: '3rem' }}>✅</div>
        <h2 style={{ color: c.primary }}>{typeLabel} creada</h2>
        <p>No. <strong>{result.full_number}</strong> · Total {money(result.total)}</p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '18px', flexWrap: 'wrap' }}>
          <button onClick={print} style={btn(c.primary, c.background)}>🖨 Imprimir</button>
          <button onClick={onBack} style={btnOutline}>Volver</button>
        </div>
        {msg && <p style={{ color: msg.ok ? c.success : c.error, marginTop: '10px' }}>{msg.text}</p>}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '1.25rem 1rem 4rem' }}>
      <button onClick={onBack} style={btnOutline}>← Volver</button>
      <h1 style={{ color: c.primary, fontSize: '1.35rem', margin: '10px 0 16px' }}>Nueva {typeLabel.toLowerCase()}</h1>

      {!factura ? (
        <section style={card}>
          <h3 style={sectionTitle}>Factura de origen</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Buscar factura (No., cliente o documento)</label>
              <input style={inputStyle} value={facturaQuery} onChange={(e) => setFacturaQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchFactura()} />
            </div>
            <button onClick={searchFactura} style={btn(c.primary, c.background)}>Buscar</button>
          </div>
          {matches && matches.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              {matches.map((f) => (
                <div key={f.id} onClick={() => pickFactura(f.id)} style={{ cursor: 'pointer', padding: '10px 12px', border: `1px solid ${c.border}`, borderRadius: '8px', marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
                  <span>No. {f.full_number} · {f.customer_name || 'Sin cliente'}</span>
                  <strong>{money(f.total)}</strong>
                </div>
              ))}
            </div>
          )}
          {msg && <p style={{ color: msg.ok ? c.success : c.error, fontSize: '0.85rem', marginTop: '8px' }}>{msg.text}</p>}
        </section>
      ) : (
        <>
          <section style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <div style={{ color: c.textMuted, fontSize: '0.8rem' }}>Factura de origen</div>
                <div>No. <strong>{factura.full_number}</strong> · {factura.customer_name || 'Sin cliente'} {factura.customer_doc ? `(${factura.customer_doc})` : ''}</div>
              </div>
              <button onClick={() => { setFactura(null); setItems([]); }} style={btnOutline}>Cambiar factura</button>
            </div>
          </section>

          <section style={card}>
            <h3 style={sectionTitle}>Motivo</h3>
            <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={motivo} onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej: Devolución parcial, corrección de valor…" />
          </section>

          <section style={card}>
            <h3 style={sectionTitle}>Líneas de la nota</h3>
            {items.map((it, i) => {
              const l = computeLine(it);
              return (
                <div key={i} style={{ border: `1px solid ${c.border}`, borderRadius: '10px', padding: '12px', marginBottom: '10px', background: c.background }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 2fr', gap: '0 12px' }}>
                    <div><label style={labelStyle}>Nombre</label><input style={inputStyle} value={it.name} onChange={(e) => setItem(i, { name: e.target.value })} /></div>
                    <div><label style={labelStyle}>Descripción</label><input style={inputStyle} value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} /></div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)', gap: '0 10px' }}>
                    <div><label style={labelStyle}>Cantidad</label><input type="number" onWheel={(e) => e.currentTarget.blur()} style={inputStyle} value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} /></div>
                    <div><label style={labelStyle}>Precio</label><input type="number" onWheel={(e) => e.currentTarget.blur()} style={inputStyle} value={it.unit_price} onChange={(e) => setItem(i, { unit_price: e.target.value })} /></div>
                    <div><label style={labelStyle}>IVA %</label><input type="number" onWheel={(e) => e.currentTarget.blur()} style={inputStyle} value={it.iva_percent} onChange={(e) => setItem(i, { iva_percent: e.target.value })} /></div>
                    <div><label style={labelStyle}>Retef. %</label><input type="number" onWheel={(e) => e.currentTarget.blur()} style={inputStyle} value={it.retefuente_percent} onChange={(e) => setItem(i, { retefuente_percent: e.target.value })} /></div>
                    <div><label style={labelStyle}>Imp.cons %</label><input type="number" onWheel={(e) => e.currentTarget.blur()} style={inputStyle} value={it.impoconsumo_percent} onChange={(e) => setItem(i, { impoconsumo_percent: e.target.value })} /></div>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '8px', fontSize: '0.82rem', color: c.textMuted, borderTop: `1px dashed ${c.border}`, paddingTop: '8px', justifyContent: 'space-between' }}>
                    <span>Subtotal: <strong style={{ color: c.text }}>{money(l.subtotal)}</strong> · Impuesto: <strong style={{ color: c.text }}>{money(l.tax)}</strong> · Total: <strong style={{ color: c.primary }}>{money(l.total)}</strong></span>
                    {items.length > 1 && <button onClick={() => removeItem(i)} style={{ ...btnOutline, borderColor: c.error, color: c.error }}>Quitar</button>}
                  </div>
                </div>
              );
            })}
          </section>

          <section style={card}>
            <div style={{ maxWidth: 320, marginLeft: 'auto', fontSize: '0.9rem' }}>
              <Row l="Subtotal" v={money(totals.subtotal)} />
              <Row l="IVA" v={money(totals.iva)} />
              {totals.impo > 0 && <Row l="Imp. consumo" v={money(totals.impo)} />}
              {totals.rete > 0 && <Row l="Retefuente (-)" v={money(totals.rete)} />}
              <div style={{ borderTop: `1px solid ${c.border}`, margin: '6px 0' }} />
              <Row l="TOTAL" v={money(totals.total)} bold />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '14px', flexWrap: 'wrap' }}>
              {msg && !msg.ok && <span style={{ color: c.error, alignSelf: 'center' }}>{msg.text}</span>}
              <button onClick={submit} disabled={busy} style={{ ...btn(c.primary, c.background), opacity: busy ? 0.6 : 1, padding: '12px 26px', fontSize: '1rem' }}>
                {busy ? 'Creando…' : `Crear ${typeLabel.toLowerCase()}`}
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Row({ l, v, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontWeight: bold ? 700 : 400, fontSize: bold ? '1.05rem' : '0.9rem', color: bold ? c.primary : c.text }}>
      <span style={{ color: bold ? c.primary : c.textMuted }}>{l}</span><span>{v}</span>
    </div>
  );
}

const card = { background: c.surface, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '18px', marginBottom: '14px' };
const sectionTitle = { color: c.text, fontSize: '1rem', margin: '0 0 12px' };
const btnOutline = { background: 'none', border: `1px solid ${c.border}`, color: c.text, borderRadius: '7px', padding: '9px 14px', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'inherit' };
function btn(bg, fg) { return { background: bg, border: 'none', color: fg, borderRadius: '7px', padding: '9px 16px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, fontFamily: 'inherit' }; }

export default NoteEditor;
